// src/views/pages/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signIn,
  resetPassword,
  confirmResetPassword,
  confirmSignIn, // Changed from completeNewPassword
} from "@aws-amplify/auth";
import { useAuth } from "../../context/AuthContext";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Container,
  Col,
  Row,
  FormFeedback,
  FormText,
} from "reactstrap";

export default function Login() {
  // toggle body class
  useEffect(() => {
    document.body.classList.toggle("login-page");
    return () => document.body.classList.toggle("login-page");
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/dashboard";
  const { login } = useAuth();

  // modes: "signin" | "forgotEntry" | "confirmCode" | "newPasswordRequired"
  const [mode, setMode] = useState("signin");

  // common
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // signin
  const [password, setPassword] = useState("");

  // forgot-entry & newPasswordRequired
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // state for NEW_PASSWORD_REQUIRED challenge
  const [cognitoUser, setCognitoUser] = useState(null); // Added

  // checklist
  const isMin8         = newPassword.length >= 8;
  const hasNumber      = /\d/.test(newPassword);
  const hasUpper       = /[A-Z]/.test(newPassword);
  const passwordsMatch = newPassword && newPassword === confirmPassword;

  // confirm-code
  const [code, setCode] = useState("");

  // Handlers
  async function handleSignIn(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // login() from useAuth() should throw an error with
      // err.code === "NewPasswordRequired" and err.cognitoUser attached
      // if a new password is required by Cognito.
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === "NewPasswordRequired" && err.cognitoUser) {
        setCognitoUser(err.cognitoUser);
        setMode("newPasswordRequired");
        setPassword(""); // Clear current password input
        // Pre-fill email if not already matching, and make it read-only in this mode
        if (err.cognitoUser.username && err.cognitoUser.username !== email) {
          setEmail(err.cognitoUser.username);
        }
        setError("Welcome! As a new user, please choose a new password to continue.");
      } else if (err.code === "UserArchived") {
        setError(err.message || "This account has been archived and cannot log in.");
      } else if (
        err.name === "PasswordResetRequiredException" ||
        err.code === "PasswordResetRequiredException" ||
        (
          err.name === "NotAuthorizedException" &&
          /incorrect username or password/i.test(err.message || "")
        )
      ) {
        setMode("forgotEntry");
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setError("This account needs a password reset. Choose a new password below and we'll email you a reset code.");
      } else {
        setError(err.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSendCode() {
    setError("");
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setMode("confirmCode");
    } catch (err) {
      setError(err.message || "Error sending code");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmCode() {
    if (!code) {
      setError("Enter the code you received");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      // back to sign in
      setMode("signin");
      setPassword(""); // Clear password field used for sign-in
      setNewPassword("");
      setConfirmPassword("");
      setCode("");
      setError("Password reset successful! Please sign in with your new password.");
    } catch (err) {
      setError(err.message || "Error confirming reset");
    } finally {
      setLoading(false);
    }
  }

  // Handler for completing the new password challenge
  async function handleCompleteNewPassword(e) {
    e.preventDefault();
    if (!newPassword || !passwordsMatch || !isMin8 || !hasNumber || !hasUpper) {
      setError("Please ensure your new password meets all criteria and matches.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // Changed to use confirmSignIn for NEW_PASSWORD_REQUIRED challenge
      await confirmSignIn({ user: cognitoUser, challengeResponse: newPassword });

      // Attempt to log in with the new credentials to establish the session in AuthContext
      try {
        await login(email, newPassword); // email is in scope, newPassword is the one just set
        navigate(from, { replace: true }); // Navigate to the intended page
        // No need to reset local component state like mode, newPassword, etc., as we are navigating away.
        // setCognitoUser(null) could be done, but component will unmount.
      } catch (loginErr) {
        // If login after password change fails, revert to asking them to sign in manually
        console.error("Login after password change failed:", loginErr);
        setMode("signin");
        setPassword(""); // Clear password field for sign-in
        setNewPassword("");
        setConfirmPassword("");
        setCognitoUser(null);

        // Check if it's an archived user error
        if (loginErr.code === "UserArchived") {
          setError(loginErr.message || "This account has been archived and cannot log in.");
        } else {
          setError(
            "Password changed, but auto-login failed. Please sign in with your new password."
          );
        }
      }
    } catch (err) {
      setError(err.message || "Failed to set new password.");
    } finally {
      setLoading(false);
    }
  }

  // whether all criteria are met
  const canSendCode = isMin8 && hasNumber && hasUpper && passwordsMatch && email;
  const canSetNewPassword = isMin8 && hasNumber && hasUpper && passwordsMatch && newPassword;


  return (
    <div className="login-page">
      <Container>
        <Row>
          <Col lg="4" md="6" className="mx-auto">
            <Card className="card-login">
              <CardHeader className="text-center">
                <h3>
                  {mode === "signin" && "Sign In"}
                  {mode === "forgotEntry" && "Set Password"}
                  {mode === "confirmCode" && "Enter Reset Code"}
                  {mode === "newPasswordRequired" && "Choose a New Password"}
                </h3>
              </CardHeader>
              <CardBody>
                {error && (
                  <FormText color={(mode === "newPasswordRequired" && error.startsWith("Welcome!")) || error.startsWith("Password successfully changed!") || error.startsWith("Password reset successful!") ? "info" : "danger"} className="text-center mb-2">
                    {error}
                  </FormText>
                )}
                <Form
                  onSubmit={
                    mode === "signin"
                      ? handleSignIn
                      : mode === "newPasswordRequired"
                      ? handleCompleteNewPassword
                      : mode === "forgotEntry" // Ensure forgot password form doesn't submit via enter on email
                      ? (e) => e.preventDefault()
                      : mode === "confirmCode" // Ensure confirm code form doesn't submit via enter on email
                      ? (e) => e.preventDefault()
                      : (e) => e.preventDefault()
                  }
                >
                  {/* Email */}
                  <InputGroup className="mb-3">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="nc-icon nc-email-85" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={mode === "newPasswordRequired" && cognitoUser?.username}
                    />
                  </InputGroup>

                  {mode === "signin" && (
                    // Password for sign in
                    <InputGroup className="mb-3">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="nc-icon nc-key-25" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </InputGroup>
                  )}

                  {(mode === "forgotEntry" || mode === "newPasswordRequired") && (
                    <>
                      {/* New Password */}
                      <InputGroup className="mb-1">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="nc-icon nc-key-25" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          type="password"
                          placeholder={mode === "newPasswordRequired" ? "New Password" : "New Password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          invalid={newPassword && (!isMin8 || !hasNumber || !hasUpper)}
                          required
                        />
                        {/* Unified feedback for password requirements */}
                        {newPassword && (!isMin8 || !hasNumber || !hasUpper) && <FormFeedback>Password must be 8+ chars, with a number & uppercase.</FormFeedback>}
                      </InputGroup>
                      <InputGroup className="mb-1">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="nc-icon nc-key-25" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          type="password"
                          placeholder={mode === "newPasswordRequired" ? "Confirm New Password" : "Confirm Password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          invalid={confirmPassword && !passwordsMatch}
                          required
                        />
                        <FormFeedback>Passwords must match.</FormFeedback>
                      </InputGroup>

                      {/* Checklist */}
                      <ul className="mb-3" style={{ listStyle: "none", paddingLeft: 0 }}>
                        <li>
                          <i
                            className={`fa fa-${isMin8 ? "check" : "times"} text-${
                              isMin8 ? "success" : "muted"
                            } mr-2`}
                          />
                          Minimum 8 characters
                        </li>
                        <li>
                          <i
                            className={`fa fa-${hasNumber ? "check" : "times"} text-${
                              hasNumber ? "success" : "muted"
                            } mr-2`}
                          />
                          At least one number
                        </li>
                        <li>
                          <i
                            className={`fa fa-${hasUpper ? "check" : "times"} text-${
                              hasUpper ? "success" : "muted"
                            } mr-2`}
                          />
                          At least one uppercase letter
                        </li>
                        <li>
                          <i
                            className={`fa fa-${passwordsMatch && newPassword ? "check" : "times"} text-${
                              passwordsMatch && newPassword ? "success" : "muted"
                            } mr-2`}
                          />
                          Passwords match
                        </li>
                      </ul>
                    </>
                  )}

                  {mode === "confirmCode" && (
                    // Code input only
                    <InputGroup className="mb-3">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="nc-icon nc-key-25" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="text"
                        placeholder="Reset Code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        required
                      />
                    </InputGroup>
                  )}
                </Form>
              </CardBody>
              <CardFooter>
                {mode === "signin" && (
                  <>
                    <Button
                      block
                      color="primary"
                      onClick={handleSignIn}
                      disabled={loading}
                      type="submit"
                    >
                      {loading ? "Signing In…" : "Login"}
                    </Button>
                    <div className="text-center mt-2">
                      <Button
                        color="link"
                        onClick={() => {
                          setMode("forgotEntry");
                          setError("");
                          setPassword(""); 
                        }}
                      >
                        Forgot Password?
                      </Button>
                    </div>
                  </>
                )}

                {mode === "forgotEntry" && (
                  <>
                    <Button
                      block
                      color="primary"
                      onClick={handleSendCode}
                      disabled={loading || !canSendCode}
                    >
                      {loading ? "Sending Code…" : "Send Code"}
                    </Button>
                    <div className="text-center mt-2">
                      <Button
                        color="link"
                        onClick={() => {
                          setMode("signin");
                          setError("");
                          setNewPassword("");
                          setConfirmPassword("");
                        }}
                      >
                        Back to Login
                      </Button>
                    </div>
                  </>
                )}

                {mode === "confirmCode" && (
                  <>
                    <Button
                      block
                      color="primary"
                      onClick={handleConfirmCode} 
                      disabled={loading || !newPassword || !confirmPassword} // Also disable if new passwords aren't filled
                    >
                      {loading ? "Resetting…" : "Reset Password"}
                    </Button>
                    <div className="text-center mt-2">
                      <Button
                        color="link"
                        onClick={() => {
                          setMode("signin");
                          setError("");
                          setNewPassword("");
                          setConfirmPassword("");
                          setCode("");
                        }}
                      >
                        Back to Login
                      </Button>
                    </div>
                  </>
                )}

                {mode === "newPasswordRequired" && (
                  <>
                    <Button
                      block
                      color="primary"
                      onClick={handleCompleteNewPassword}
                      disabled={loading || !canSetNewPassword}
                      type="submit"
                    >
                      {loading ? "Setting Password…" : "Set New Password & Login"}
                    </Button>
                    {/* No "Back to Login" here as this is a required step */}
                  </>
                )}
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* background image */}
      <div
        className="full-page-background"
        style={{
          backgroundImage: `url(${require("assets/img/bg/beverage.png")})`,
        }}
      />
    </div>
  );
}
